
const AUTH0_DOMAIN = process.env.VUE_APP_AUTH0_DOMAIN
const AUTH0_CLIENT_ID = process.env.VUE_APP_AUTH0_CLIENT_ID
const AUTH0_API_AUDIENCE = process.env.VUE_APP_AUTH0_API_AUDIENCE

import notifications from '../notifications.js'

import { SETTINGS, setAuthHeadersCallback } from '../api/config.js'
import { createAuth0Client } from '@auth0/auth0-spa-js'

import { merge } from 'lodash'

var _vars = {
  isEnabled: true,
  vueRouter: undefined,
  auth0Client: undefined,
  auth0InitPromise: undefined,
  auth0ApiToken: undefined,
}

// Initialize Auth0 client
async function initAuth0() {
  if (!_vars.isEnabled) {
    return
  }
  console.log('Running auth0 initAuth0()')
  try {
    _vars.auth0InitPromise = createAuth0Client({
      domain: AUTH0_DOMAIN,
      clientId: AUTH0_CLIENT_ID,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: AUTH0_API_AUDIENCE,
      },
    })
    _vars.auth0Client = await _vars.auth0InitPromise

    setAuthHeadersCallback(auth0DjangoJwtApiAuthHeaders)

    // Check if user is returning from login
    if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
      await handleRedirectCallback()
    }
  } catch (err) {
    showError(err)
  }
}

async function disableService() {
  _vars.isEnabled = false
  _vars.auth0InitPromise = Promise.resolve({
    isAuthenticated: () => true,
  })
}

async function handleRedirectCallback() {
  try {
    const { appState } = await _vars.auth0Client.handleRedirectCallback()
    const returnTo = appState?.returnTo || '/'

    // Clean up the URL to remove query parameters
    window.history.replaceState({}, '', returnTo)
    if (_vars.vueRouter && appState.vueRouterPath) {
      if (_vars.vueRouter.name !== appState.vueRouterName) {
        _vars.vueRouter.replace({ path: appState.vueRouterPath })
      }
    }
  } catch (err) {
    showError(err)
  }
}

function showError(err) {
  console.error(err.message)
}

function getPlaceholderImage() {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='110' height='110' viewBox='0 0 110 110'%3E%3Ccircle cx='55' cy='55' r='55' fill='%2363b3ed'/%3E%3Cpath d='M55 50c8.28 0 15-6.72 15-15s-6.72-15-15-15-15 6.72-15 15 6.72 15 15 15zm0 7.5c-10 0-30 5.02-30 15v3.75c0 2.07 1.68 3.75 3.75 3.75h52.5c2.07 0 3.75-1.68 3.75-3.75V72.5c0-9.98-20-15-30-15z' fill='%23fff'/%3E%3C/svg%3E`
}

async function getUser() {
  let client = await _vars.auth0InitPromise
  return client.getUser()
}

async function getTokenSilently(client) {
  if (client === undefined) {
    client = await _vars.auth0InitPromise
  }

  try {
    const token = await client.getTokenSilently({
      authorizationParams: {
        audience: AUTH0_API_AUDIENCE,
      }
    })
    return token
  }
  catch (error) {
    if (error.text == 'login_required') {
      loginWithRedirect()
    }
    else if (error.error == 'invalid_grant') {
      loginWithRedirect()
    }
    else {
      console.error(error)
      notifications.pushError('Something went wrong with user authentication.', error)
    }
  }
}

async function forceRelogin() {
  try {
    await loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
      }
    })
  }
  catch (error) {
    console.error(error)
    notifications.pushError('Something went wrong with redirecting to user login', error)
  }
}

async function auth0DjangoJwtApiAuthHeaders() {
  let accessToken = await getTokenSilently()
  return {
    'Authorization': `Bearer ${accessToken}`,
  }
}

async function checkSession() {
  let client = await _vars.auth0InitPromise
  return client.checkSession()
}

async function isAuthenticated() {
  let client = await _vars.auth0InitPromise
  return client.isAuthenticated()
}

async function logout(params) {
  if (params === undefined) {
    params = {
      logoutParams: {
        returnTo: SETTINGS.logoutRedirect,
      }
    }
  }
  let client = await _vars.auth0InitPromise
  return client.logout(params)
}

async function loginWithRedirect(additionalParams) {
  let params = {
    appState: {
      returnTo: window.location.pathname + window.location.search,
      vueRouterName: _vars.vueRouter.name,
      vueRouterPath: _vars.vueRouter.currentRoute.path,
    }
  }
  let finalParams
  if (additionalParams) {
    finalParams = merge(params, additionalParams)
  }
  else {
    finalParams = params
  }
  let client = await _vars.auth0InitPromise
  return client.loginWithRedirect(finalParams)
}

async function loginWithPopup() {
  let client = await _vars.auth0InitPromise
  return client.loginWithPopup()
}

function isEnabled() {
  return _vars.isEnabled
}

function setVueRouter(vueRouter) {
  _vars.vueRouter = vueRouter
}

function get() {
  if (!_vars.auth0InitPromise) {
    _vars.auth0InitPromise = initAuth0()
      .then(() => {
        return _vars.auth0Client
      })
      .catch(error => {
        showError(error)
        _vars.auth0InitPromise = undefined
      })
  }
  return _vars.auth0InitPromise
}

export default {
  get,
  initAuth0,
  isEnabled,
  setVueRouter,
  disableService,
  logout,
  loginWithRedirect,
  loginWithPopup,
  handleRedirectCallback,
  getPlaceholderImage,
  getUser,
  checkSession,
  isAuthenticated,
  getTokenSilently,
  forceRelogin,
  showError,
}
